// useNotificationsStore — global Zustand store for in-app notifications.
// Backs both the header bell/dropdown and the full /notifications page.
//
// Data strategy (matches the rest of the app, e.g. useProjectsStore + MyProjects):
// we fetch the FULL list once and keep it in `items`. Searching and pagination
// then happen entirely on the client in the components.
import { create } from "zustand";
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from "../services/notificationsApi";

// Notifications are fetched a page at a time (newest first) via cursor
// pagination; the full-page list offers a "Load more" to pull older pages.
const PAGE_LIMIT = 30;

const useNotificationsStore = create((set, get) => ({
  // ── STATE ──────────────────────────────────────────────────────────────
  items: [], // loaded notifications (newest first), grown by loadMore()
  unreadCount: 0, // badge value — sourced from the server, not derived from items
  loading: false,
  loadingMore: false,
  hasMore: false, // are there older pages beyond what's loaded?
  nextCursor: null, // id cursor for the next page
  error: null,
  pulse: 0, // bumped on each live arrival; the bell watches this to flash

  // ── LOADING ────────────────────────────────────────────────────────────
  // Fetch the first page. `silent` skips the loading flag — used for background
  // resyncs (reconnect, tab re-focus) so the UI doesn't flash a loading state.
  // The unread badge is refreshed from the dedicated endpoint (the count can
  // exceed what's on the first page, so we never derive it from `items`).
  loadAll: async (silent = false) => {
    if (!silent) set({ loading: true });
    set({ error: null });
    try {
      const data = await fetchNotifications(PAGE_LIMIT);
      const all = data.notifications || [];
      set({
        items: all,
        hasMore: !!data.has_more,
        nextCursor: data.next_cursor ?? null,
        loading: false,
      });
      get().refreshUnreadCount();
    } catch (err) {
      set({ loading: false, error: "Failed to load notifications." });
    }
  },

  // Append the next (older) page. No-op if a page is already loading or there
  // are no more pages.
  loadMore: async () => {
    const { hasMore, loadingMore, nextCursor } = get();
    if (!hasMore || loadingMore || nextCursor == null) return;
    set({ loadingMore: true });
    try {
      const data = await fetchNotifications(PAGE_LIMIT, nextCursor);
      const more = data.notifications || [];
      set((state) => {
        const seen = new Set(state.items.map((n) => n.id));
        return {
          items: [...state.items, ...more.filter((n) => !seen.has(n.id))],
          hasMore: !!data.has_more,
          nextCursor: data.next_cursor ?? null,
          loadingMore: false,
        };
      });
    } catch {
      set({ loadingMore: false });
    }
  },

  refreshUnreadCount: async () => {
    try {
      const count = await fetchUnreadCount();
      set({ unreadCount: count });
    } catch {
      /* non-fatal */
    }
  },

  // ── REALTIME ───────────────────────────────────────────────────────────
  // Called by the websocket hook when a new notification arrives.
  pushIncoming: (notification) => {
    if (!notification || notification.id == null) return;
    set((state) => {
      if (state.items.some((n) => n.id === notification.id)) return {};
      return {
        items: [notification, ...state.items],
        unreadCount: state.unreadCount + (notification.is_read ? 0 : 1),
        pulse: state.pulse + 1, // trigger the bell flash
      };
    });
  },
  setUnreadCount: (n) => set({ unreadCount: Math.max(0, n || 0) }),

  // ── MUTATIONS (optimistic) ───────────────────────────────────────────────
  markRead: async (id) => {
    const target = get().items.find((n) => n.id === id);
    if (!target || target.is_read) return;
    set((state) => ({
      items: state.items.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));
    try {
      await markNotificationRead(id);
    } catch {
      get().refreshUnreadCount(); // reconcile on failure
    }
  },

  markAllRead: async () => {
    set((state) => ({
      items: state.items.map((n) => ({ ...n, is_read: true })),
      unreadCount: 0,
    }));
    try {
      await markAllNotificationsRead();
    } catch {
      get().refreshUnreadCount();
    }
  },

  remove: async (id) => {
    const target = get().items.find((n) => n.id === id);
    const wasUnread = target && !target.is_read;
    set((state) => ({
      items: state.items.filter((n) => n.id !== id),
      unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
    }));
    try {
      await deleteNotification(id);
    } catch {
      get().refreshUnreadCount();
    }
  },

  // Reset everything (e.g. on logout / org switch).
  reset: () =>
    set({
      items: [],
      unreadCount: 0,
      loading: false,
      loadingMore: false,
      hasMore: false,
      nextCursor: null,
      error: null,
      pulse: 0,
    }),
}));

// New Discover matches: the count on the unread "new tenders fit your business"
// entry the nightly run left, or 0. Shared by the sidebar and Tender Search's
// mode switch so the two never disagree.
export const selectNewTenderMatches = (s) => {
  const n = s.items.find((x) => x.type === "tender_matches" && !x.is_read);
  return n ? n.data?.count || 1 : 0;
};

export default useNotificationsStore;
